// react
import { useEffect, useRef } from 'react';
import type { Step } from 'react-joyride';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute, useParams, useSearch } from '@tanstack/react-router';

// material ui
import { Tooltip } from '@mui/material';

// api
import type {
  GatewaysGetCollectionAsyncByqueryOptionsApiArg,
  ReadGatewayDto,
} from '@/api/generated/recalc/endpoints/v1/gateways';
import { useGatewaysGetCollectionAsyncByqueryOptionsQuery } from '@/api/generated/recalc/endpoints/v1/gateways';

// shared components
import { WritableOnly } from '@/components/access-control';
import { getGatewayOverviewTour } from '@/components/joyride/guided-tours/GatewayOverview.tour';
import useAppTour from '@/components/joyride/useAppTour';
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';
import { CreateButtonLink } from '@/components/ui/button';
import LinearLoadingIndicator from '@/components/ui/progress/LinearLoadingIndicator';

// feature-components
import GatewayDataGrid from '@/features/gateways/components/GatewayDataGrid';

import { skipToken } from '@reduxjs/toolkit/query';

export const Route = createFileRoute('/$accountId/_user/gateways/')({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation('units-gateways-sensors');
  const search = useSearch({ strict: false });
  const hasAutoStartedTourRef = useRef(false);
  const { accountId } = useParams({ strict: false });

  const { controls, Tour } = useAppTour({
    steps: getGatewayOverviewTour() as Step[],
  });

  useEffect(() => {
    if (search?.tour === 'gateways' && !hasAutoStartedTourRef.current) {
      hasAutoStartedTourRef.current = true;
      controls.start();
    } else if (!search?.tour) {
      controls.close();
    }
  }, [controls, search?.tour]);

  const queryArg:
    | GatewaysGetCollectionAsyncByqueryOptionsApiArg
    | typeof skipToken = accountId
    ? { 'Account.AccountId': `eq(${accountId})` }
    : skipToken;

  const {
    data: gateways,
    isLoading,
    isFetching,
  } = useGatewaysGetCollectionAsyncByqueryOptionsQuery(queryArg);

  const gatewayRows: ReadGatewayDto[] | undefined = gateways;

  return (
    <>
      <LinearLoadingIndicator isLoading={isLoading || isFetching} />
      <PageContainer
        maxWidth="xl"
        sx={{ py: 2 }}
        data-testid="gateway-overview-container"
      >
        {Tour}
        <PageHeader title={t('gateways.gatewayOverview')} disableArrow={true}>
          <WritableOnly>
            <Tooltip title={t('gateways.registerGateway')} placement="top">
              <CreateButtonLink to="create" data-tour="gateway-overview-add">
                {t('gateways.registerGateway', 'Add')}
              </CreateButtonLink>
            </Tooltip>
          </WritableOnly>
        </PageHeader>

        <GatewayDataGrid
          accountId={accountId}
          gateways={gatewayRows}
          isLoading={isLoading}
        />
      </PageContainer>
    </>
  );
}
